import { z } from 'zod';
import { isoDateSchema } from '../primitives/branded.js';
import { positivePriceSchema } from '../primitives/numeric.js';
import { timestampSchema } from '../primitives/timestamp.js';
/**
 * Einbuchung eines Stop-Loss beim Broker.
 *
 * Die Anwendung ermittelt Stop-Kurse, sie stellt sie aber nicht beim Broker ein —
 * das geschieht von Hand. Eine Einbuchung hält fest, **welcher Kurs dort
 * tatsächlich als Order liegt** und **bis wann sie gilt**. Ohne diese Angabe wäre
 * ein berechneter Stop nur eine Zahl auf dem Schirm, die niemanden schützt.
 *
 * Daraus ergibt sich, wann Handlungsbedarf besteht: keine Order hinterlegt, ihre
 * Gültigkeit abgelaufen, oder der berechnete Kurs ist inzwischen ein anderer als
 * der eingebuchte.
 */
export const stopLossBookingSchema = z.object({
    /** Der Kurs, der beim Broker als Order liegt. */
    stopPrice: positivePriceSchema,
    /** Letzter Tag, an dem die Order gilt. */
    validUntil: isoDateSchema,
    /** Zeitpunkt der Einbuchung. */
    bookedAt: timestampSchema,
});
/**
 * Zustand der Einbuchung.
 *
 * Die Reihenfolge ist zugleich die Dringlichkeit: `not_booked` und `expired`
 * bedeuten, dass die Position derzeit **ungeschützt** ist; `diverged` heißt, sie
 * ist geschützt, aber nicht auf dem beabsichtigten Niveau.
 */
export const bookingStatusSchema = z.enum(['not_booked', 'expired', 'diverged', 'active']);
export const BOOKING_STATUS_LABELS = {
    not_booked: 'Nicht eingebucht',
    expired: 'Einbuchung abgelaufen',
    diverged: 'Stop-Loss geändert',
    active: 'Eingebucht',
};
/** Erläuterung für die Oberfläche, als Titel eines Hinweises. */
export const BOOKING_STATUS_HINTS = {
    not_booked: 'Für diesen Wert liegt keine Stop-Loss-Order beim Broker.',
    expired: 'Die Gültigkeit der Stop-Loss-Order ist abgelaufen.',
    diverged: 'Der berechnete Stop-Loss weicht von der eingebuchten Order ab.',
    active: 'Die Stop-Loss-Order liegt beim Broker und ist gültig.',
};
/**
 * Toleranz beim Vergleich von eingebuchtem und berechnetem Kurs.
 *
 * Ein Cent. Enger wäre unpraktisch, weil Fließkomma-Arithmetik und die
 * Dezimalspalten der Datenbank minimal voneinander abweichen können; weiter wäre
 * gefährlich, weil eine echte Abweichung unbemerkt bliebe.
 */
export const BOOKING_PRICE_TOLERANCE = 0.01;
/**
 * Leitet den Zustand aus Einbuchung, aktuellem Stop-Kurs und Datum ab.
 *
 * Bewusst eine reine Funktion in `shared`: Der Server liefert den Zustand mit der
 * Position aus, und die Oberfläche kann ihn nach einer Änderung sofort selbst
 * bestimmen, ohne auf eine Antwort zu warten. Zwei Implementierungen würden früher
 * oder später auseinanderlaufen.
 */
export const deriveBookingStatus = (booking, currentStopPrice, today) => {
    if (!booking)
        return 'not_booked';
    if (booking.validUntil < today)
        return 'expired';
    if (currentStopPrice === null)
        return 'active';
    const diverged = Math.abs(booking.stopPrice - currentStopPrice) > BOOKING_PRICE_TOLERANCE;
    return diverged ? 'diverged' : 'active';
};
/** Eingabe beim Einbuchen: Der Kurs stammt aus der aktuellen Auswertung. */
export const createBookingSchema = z.object({
    validUntil: isoDateSchema,
});
//# sourceMappingURL=booking.js.map